import { create } from 'zustand';
import type { EngineSnapshot, Difficulty } from '../engine/engine';
import type { OperatorAction } from '../engine/core/actions';
import type { TagMeta } from '../engine/core/tags';
import type { EngineEvent } from '../engine/core/events';
import type { TrendSeries } from '../engine/core/trends';
import type { FromWorker, InitOptions, ToWorker, WorkerStats } from './protocol';
import type { PlantConfig } from '../engine/plant/types';
import { plantById } from '../plants';

export interface WindowState {
  id: string;
  kind: 'faceplate' | 'steptest' | 'alarmInfo' | 'interlock' | 'sequence';
  ref: string;
  x: number;
  y: number;
  z: number;
  minimised?: boolean;
}

export type ScreenId = 'overview' | 'alarms' | 'trends' | 'events' | 'kpi' | 'sandbox' | 'lesson';

interface EngineState {
  ready: boolean;
  error: string | null;
  plant: PlantConfig | null;
  meta: TagMeta[];
  indexOf: Map<string, number>;
  trendTags: string[];
  snap: EngineSnapshot | null;
  /**
   * The running event log. Accumulated here rather than in the Event Log component,
   * which only mounts when the operator opens that screen and would otherwise miss
   * everything that happened before.
   */
  events: EngineEvent[];
  /** Bumped on every snapshot so components can subscribe cheaply. */
  tick: number;
  values: Float64Array;
  quality: Uint8Array;
  running: boolean;
  speed: number;
  stats: WorkerStats;
  /** True while a replay is being reconstructed in the worker. */
  replaying: boolean;
  replayProgress: number;
  seed: number;
  scenarioId: string | null;
  lessonId: string | null;
  difficulty: Difficulty;

  // UI state
  screen: ScreenId;
  view: string;
  windows: WindowState[];
  topZ: number;
  hornMuted: boolean;
  showBanner: boolean;
  selectedTrendGroup: string;
  customPens: string[];

  // Actions
  attach(worker: Worker): void;
  boot(opts: InitOptions): void;
  /** Replay this session's own action log from the start, at full speed. */
  replay(): Promise<void>;
  send(action: OperatorAction): void;
  play(): void;
  pause(): void;
  setSpeed(speed: number): void;
  stepOnce(steps: number): void;
  requestTrend(tags: string[], from: number, to: number, maxPoints: number): Promise<TrendSeries[]>;
  requestSave(): Promise<string>;
  setScreen(s: ScreenId): void;
  setView(v: string): void;
  openWindow(kind: WindowState['kind'], ref: string): void;
  closeWindow(id: string): void;
  moveWindow(id: string, x: number, y: number): void;
  focusWindow(id: string): void;
  setTrendGroup(id: string): void;
  setCustomPens(tags: string[]): void;
  toggleHorn(): void;
}

/** How many events the UI keeps. The engine's own ring is larger. */
const MAX_EVENTS = 5000;

let worker: Worker | null = null;
let trendSeq = 0;
const trendWaiters = new Map<number, (s: TrendSeries[]) => void>();
let saveSeq = 0;
const saveWaiters = new Map<number, (s: string) => void>();

const post = (m: ToWorker, transfer?: Transferable[]): void => {
  worker?.postMessage(m, transfer ?? []);
};

export const useEngine = create<EngineState>((set, get) => ({
  ready: false,
  error: null,
  plant: null,
  meta: [],
  indexOf: new Map(),
  trendTags: [],
  snap: null,
  events: [],
  tick: 0,
  values: new Float64Array(0),
  quality: new Uint8Array(0),
  running: false,
  speed: 1,
  stats: { achievedSpeed: 0, behind: false, stepMicros: 0, trendBytes: 0 },
  replaying: false,
  replayProgress: 0,
  seed: 0,
  scenarioId: null,
  lessonId: null,
  difficulty: 'operator',

  screen: 'overview',
  view: 'overview',
  windows: [],
  topZ: 10,
  hornMuted: false,
  showBanner: true,
  selectedTrendGroup: '',
  customPens: [],

  attach(w) {
    worker = w;
    w.onmessage = (ev: MessageEvent<FromWorker>): void => {
      const m = ev.data;
      switch (m.t) {
        case 'ready': {
          const indexOf = new Map<string, number>();
          m.meta.forEach((t) => indexOf.set(t.name, t.id));
          set({
            ready: true,
            meta: m.meta,
            indexOf,
            trendTags: m.trendTags,
            seed: m.seed,
            scenarioId: m.scenarioId,
            error: null,
          });
          break;
        }
        case 'snapshot': {
          const prev = get();
          // Return the previous buffers to the worker's pool.
          const vb = prev.values.buffer as ArrayBuffer;
          const qb = prev.quality.buffer as ArrayBuffer;
          if (prev.values.length > 0 && vb.byteLength > 0) {
            post({ t: 'recycle', values: vb, quality: qb }, [vb, qb]);
          }
          set({
            snap: m.snap,
            values: m.snap.values,
            quality: m.snap.quality,
            running: m.running,
            speed: m.speed,
            stats: m.stats,
            tick: prev.tick + 1,
            events:
              m.snap.events.length > 0
                ? [...prev.events, ...m.snap.events].slice(-MAX_EVENTS)
                : prev.events,
          });
          break;
        }
        case 'trend': {
          trendWaiters.get(m.id)?.(m.series);
          trendWaiters.delete(m.id);
          break;
        }
        case 'save': {
          saveWaiters.get(m.id)?.(m.data);
          saveWaiters.delete(m.id);
          break;
        }
        case 'replay':
          set({
            replaying: !m.progress.done,
            replayProgress: m.progress.total > 0 ? m.progress.step / m.progress.total : 1,
          });
          break;
        case 'error':
          set({ error: m.text });
          break;
      }
    };
  },

  boot(opts) {
    set({
      ready: false,
      events: [],
      plant: plantById(opts.plantId) ?? null,
      scenarioId: opts.scenarioId,
      lessonId: opts.lessonId ?? null,
      seed: opts.seed,
      difficulty: opts.difficulty,
      windows: [],
      screen: 'overview',
      view: 'overview',
      selectedTrendGroup: plantById(opts.plantId)?.trendGroups?.[0]?.id ?? '',
    });
    post({ t: 'init', ...opts });
  },

  send(action) {
    post({ t: 'action', action });
  },
  play() {
    post({ t: 'play' });
    set({ running: true });
  },
  pause() {
    post({ t: 'pause' });
    set({ running: false });
  },
  setSpeed(speed) {
    post({ t: 'setSpeed', speed });
    set({ speed });
  },
  stepOnce(steps) {
    post({ t: 'stepOnce', steps });
  },

  requestTrend(tags, from, to, maxPoints) {
    const id = ++trendSeq;
    return new Promise<TrendSeries[]>((resolve) => {
      trendWaiters.set(id, resolve);
      post({ t: 'getTrend', id, tags, from, to, maxPoints });
    });
  },

  async replay() {
    const state = get();
    if (!state.plant || !state.snap) return;
    const raw = await state.requestSave();
    const save = JSON.parse(raw) as { actionLog?: { entries: { step: number; action: OperatorAction }[] } };
    const entries = save.actionLog?.entries ?? [];
    set({ replaying: true, events: [] });
    post({
      t: 'init',
      plantId: state.plant.id,
      scenarioId: state.scenarioId,
      lessonId: state.lessonId,
      seed: state.seed,
      difficulty: state.difficulty,
      replay: { entries, toStep: state.snap.stepCount },
    });
  },

  requestSave() {
    const id = ++saveSeq;
    return new Promise<string>((resolve) => {
      saveWaiters.set(id, resolve);
      post({ t: 'save', id });
    });
  },

  setScreen(screen) {
    set({ screen });
  },
  setView(view) {
    set({ view });
  },
  openWindow(kind, ref) {
    const { windows, topZ } = get();
    const id = `${kind}:${ref}`;
    const existing = windows.find((w) => w.id === id);
    if (existing) {
      set({
        windows: windows.map((w) => (w.id === id ? { ...w, z: topZ + 1, minimised: false } : w)),
        topZ: topZ + 1,
      });
      return;
    }
    const n = windows.length;
    set({
      windows: [
        ...windows,
        { id, kind, ref, x: 120 + (n % 6) * 34, y: 110 + (n % 6) * 30, z: topZ + 1 },
      ],
      topZ: topZ + 1,
    });
  },
  closeWindow(id) {
    set({ windows: get().windows.filter((w) => w.id !== id) });
  },
  moveWindow(id, x, y) {
    set({ windows: get().windows.map((w) => (w.id === id ? { ...w, x, y } : w)) });
  },
  focusWindow(id) {
    const { windows, topZ } = get();
    set({
      windows: windows.map((w) => (w.id === id ? { ...w, z: topZ + 1 } : w)),
      topZ: topZ + 1,
    });
  },
  setTrendGroup(id) {
    set({ selectedTrendGroup: id });
  },
  setCustomPens(tags) {
    set({ customPens: tags });
  },
  toggleHorn() {
    set({ hornMuted: !get().hornMuted });
  },
}));

// ── Selectors ───────────────────────────────────────────────────────────────

/**
 * A single frozen empty array, shared by every selector that needs a fallback.
 * Returning a fresh `[]` from a zustand selector changes identity on every render and
 * sends React's external-store subscription into an infinite loop.
 */
export const EMPTY: readonly never[] = Object.freeze([]);

/** Live value of a tag by name. Returns 0 for an unknown tag. */
export function useTag(name: string): number {
  return useEngine((s) => {
    const i = s.indexOf.get(name);
    return i === undefined ? 0 : (s.values[i] ?? 0);
  });
}

export function useTagMeta(name: string): TagMeta | undefined {
  return useEngine((s) => {
    const i = s.indexOf.get(name);
    return i === undefined ? undefined : s.meta[i];
  });
}

export function useLoop(id: string): EngineSnapshot['loops'][number] | undefined {
  return useEngine((s) => s.snap?.loops.find((l) => l.id === id));
}

/** Read a tag outside React (renderers, canvas draw loops). */
export function readTag(name: string): number {
  const s = useEngine.getState();
  const i = s.indexOf.get(name);
  return i === undefined ? 0 : (s.values[i] ?? 0);
}

export function formatTag(name: string, value: number, decimals?: number): string {
  const s = useEngine.getState();
  const i = s.indexOf.get(name);
  const d = decimals ?? s.meta[i ?? -1]?.decimals ?? 2;
  if (!Number.isFinite(value)) return '——';
  return value.toFixed(d);
}
